import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';

import { MantineProvider } from '@mantine/core';
import { Worker } from '@react-pdf-viewer/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { PageLoadMonitor } from 'src/components/common/PageLoadMonitor';
import {
  AppContextProvider,
  AppTelemetryProvider,
  useAppContext,
} from 'src/contexts/AppContext';
import { BulkEditHubContextProvider } from 'src/contexts/BulkEditHubContext';
import { ClientLogContextProvider } from 'src/contexts/ClientLogContext';
import { SiteContentProvider } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { EmailPurchaseOrderContextProvider } from 'src/contexts/EmailPurchaseOrderContext/EmailPurchaseOrderContext';
import { FileExportContextProvider } from 'src/contexts/FileExportContext';
import { GenericDialogProvider } from 'src/contexts/GenericDialogContext/GenericDialogContext';
import { LocalizationContextProvider } from 'src/contexts/LocalizationContext';
import { NotificationsContextProvider } from 'src/contexts/NotificationsContext';
import { PageLoadStatusProvider } from 'src/contexts/PageLoadStatusContext';
import { ScreenSizeProvider } from 'src/contexts/ScreenSizeContext/ScreenSizeContext';
import { SellerAccountContextProvider } from 'src/contexts/SellerAccountContext';
import { SellerRoleContextProvider } from 'src/contexts/SellerRoleContext';
import { SellerUserAllSettingsProvider } from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext';
import {
  SiteThemeContextProvider,
  useSiteTheme,
} from 'src/contexts/SiteTheme/SiteThemeContext';
import { SiteTimezoneContextProvider } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { ThemeUtils } from 'src/core/themes';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { DefaultTheme, ThemeProvider } from 'styled-components/macro';

import * as styles from './App.css';
import { AppContent } from './AppContent';
import { MainQueryClient } from './QueryClients';

const touchBackendOptions = { enableMouseEvents: true };

function AppLayout() {
  const { appContext, cobrandId, mainRoute } = useAppContext();
  const { isDarkMode } = useSiteTheme();
  const [legacyTheme, setLegacyTheme] = useState<DefaultTheme>();

  const [isThemeLoading, setIsThemeLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      setIsThemeLoading(true);
      try {
        await ThemeUtils.adjustBodyThemeClasses(cobrandId, isDarkMode);
        const theme = await ThemeUtils.getLegacyThemeForCoBrand(
          cobrandId,
          isDarkMode
        );
        setLegacyTheme(theme);
      } catch (error) {
        console.error('Failed to load theme:', error);
        // Set a fallback theme or handle error
      } finally {
        setIsThemeLoading(false);
      }
    };

    loadTheme();
  }, [cobrandId, isDarkMode]);

  if (!appContext || isThemeLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <PosSpinner />
      </div>
    );
  }

  return (
    <ThemeProvider theme={legacyTheme!}>
      <MantineProvider theme={{ fontFamily: "'Inter', sans-serif" }}>
        <ScreenSizeProvider>
          <DialogProvider>
            <GenericDialogProvider>
              <ClientLogContextProvider>
                <PageLoadStatusProvider>
                  {/* Everthing below depends on the error-boundery context */}
                  <SiteContentProvider>
                    <SellerUserAllSettingsProvider>
                      <SiteTimezoneContextProvider>
                        <LocalizationContextProvider>
                          <SellerAccountContextProvider>
                            <SellerRoleContextProvider>
                              <EmailPurchaseOrderContextProvider>
                                {/* See https://github.com/react-dnd/react-dnd/issues/3257#issuecomment-1239254032 */}
                                <DndProvider
                                  backend={TouchBackend}
                                  options={touchBackendOptions}
                                >
                                  <NotificationsContextProvider
                                    loadActiveNotifications={
                                      mainRoute === MainRoute.Home ||
                                      mainRoute === MainRoute.Notifications
                                    }
                                  >
                                    <FileExportContextProvider>
                                      <BulkEditHubContextProvider>
                                        <DialogProvider>
                                          <GenericDialogProvider>
                                            {/* Required for pdf viewer to work https://react-pdf-viewer.dev/docs/basic-usage/ */}
                                            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                              <AppContent />
                                            </Worker>
                                          </GenericDialogProvider>
                                        </DialogProvider>
                                      </BulkEditHubContextProvider>
                                    </FileExportContextProvider>
                                  </NotificationsContextProvider>
                                </DndProvider>
                              </EmailPurchaseOrderContextProvider>
                            </SellerRoleContextProvider>
                          </SellerAccountContextProvider>
                        </LocalizationContextProvider>
                        <PageLoadMonitor />
                      </SiteTimezoneContextProvider>
                    </SellerUserAllSettingsProvider>
                  </SiteContentProvider>
                </PageLoadStatusProvider>
              </ClientLogContextProvider>
            </GenericDialogProvider>
          </DialogProvider>
        </ScreenSizeProvider>
      </MantineProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <AppTelemetryProvider>
      <QueryClientProvider {...MainQueryClient}>
        <AppContextProvider>
          <SiteThemeContextProvider>
            <AppLayout />
          </SiteThemeContextProvider>
        </AppContextProvider>
      </QueryClientProvider>
    </AppTelemetryProvider>
  );
}

export default App;
